/**
 * RETOBLOCK PYTHON GUIDE DRAWER
 * Ngăn kéo hướng dẫn lập trình Python trượt từ cạnh phải màn hình.
 * Thiết kế tối ưu cho iPad / Máy tính bảng / Màn hình cảm ứng:
 * - Không chuyển tab, giữ Web Bluetooth (BLE) 100% liên tục không bao giờ bị đứt kết nối.
 * - Hỗ trợ tra cứu nhanh lệnh dùng chung & lệnh riêng theo từng bộ kit (RETOLOGIX).
 * - Sao chép code mẫu 1-chạm (One-touch Copy) với hiệu ứng phản hồi trực quan.
 * - Tự động đồng bộ Supabase Database-First qua BroadcastChannel & fetchGuideData.
 */

import { fetchGuideData, getGuideData, SYNC_CHANNEL } from '../docs/python-guide-data.js';

export class PythonGuideDrawer {
  constructor(options = {}) {
    this.container = options.container || document.getElementById('python-guide-drawer-container');
    this.isOpen = false;
    this.data = getGuideData();
    this.activeSection = 'kit'; // 'kit' hoặc 'general'
    this.selectedKitId = 'RETOLOGIX';
    this.searchKeyword = '';

    this.init();
  }

  init() {
    if (!this.container) return;
    this.renderShell();
    this.bindEvents();
    this.loadData();

    // Lắng nghe đồng bộ dữ liệu đa tab khi Admin chỉnh sửa trên Supabase
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        const channel = new BroadcastChannel(SYNC_CHANNEL);
        channel.onmessage = async () => {
          await this.loadData();
        };
      } catch (e) {}
    }
  }

  async loadData() {
    try {
      const freshData = await fetchGuideData();
      if (freshData && freshData.kits && freshData.generalSection) {
        this.data = freshData;
        this.updateKitOptions();
        this.renderBody();
      }
    } catch (err) {
      console.warn('[PythonGuideDrawer] Lỗi nạp dữ liệu:', err);
    }
  }

  renderShell() {
    this.container.innerHTML = `
      <div class="pyguide-drawer-overlay" id="pyguide-drawer-overlay">
        <div class="pyguide-drawer-panel" id="pyguide-drawer-panel" role="dialog" aria-modal="true" aria-label="Hướng dẫn lập trình Python">
          
          <!-- 1. Header -->
          <div class="pyguide-drawer-header">
            <div class="pyguide-drawer-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#38BDF8" stroke-width="2.2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
              <span>Lệnh Python</span>
              <span class="pyguide-ble-safe-badge" title="Mở ngăn kéo ngay trong ứng dụng giúp kết nối BLE Bluetooth với robot không bị ngắt quãng">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm-2 15-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8z"/></svg>
                BLE Safe
              </span>
            </div>

            <div class="pyguide-drawer-header-actions">
              <a href="/python-guide.html" target="_blank" class="pyguide-icon-btn" title="Mở trang tài liệu đầy đủ ở tab mới (Desktop)">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                  <polyline points="15 3 21 3 21 9"/>
                  <line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
              </a>
              <button class="pyguide-icon-btn" id="btn-pyguide-drawer-close" type="button" title="Đóng ngăn kéo (Esc)">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- 2. Toolbar (Tìm kiếm + Bộ chọn phân hệ) -->
          <div class="pyguide-drawer-toolbar">
            <div class="pyguide-drawer-search-wrapper">
              <svg class="pyguide-drawer-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.3-4.3"/>
              </svg>
              <input type="text" id="pyguide-drawer-search-input" class="pyguide-drawer-search-input" placeholder="Tìm lệnh (move, distance, while...)" />
            </div>

            <div class="pyguide-drawer-nav-row">
              <div class="pyguide-segmented-control">
                <button type="button" class="pyguide-segment-btn active" id="btn-drawer-sec-kit">Theo Kit</button>
                <button type="button" class="pyguide-segment-btn" id="btn-drawer-sec-general">Dùng Chung</button>
              </div>

              <select class="pyguide-drawer-kit-select" id="pyguide-drawer-kit-select" title="Chọn bộ kit"></select>
            </div>
          </div>

          <!-- 3. Nội dung cuộn danh sách lệnh -->
          <div class="pyguide-drawer-body" id="pyguide-drawer-body"></div>
        </div>
      </div>
    `;

    this.updateKitOptions();
    this.renderBody();
  }

  updateKitOptions() {
    const kitSelect = document.getElementById('pyguide-drawer-kit-select');
    if (!kitSelect || !this.data.kits) return;

    kitSelect.innerHTML = this.data.kits.map(k => `
      <option value="${this.escapeHtml(k.id)}" ${k.id === this.selectedKitId ? 'selected' : ''}>
        ${this.escapeHtml(k.name)}
      </option>
    `).join('');

    kitSelect.style.display = this.activeSection === 'kit' ? 'block' : 'none';
  }

  bindEvents() {
    const overlay = document.getElementById('pyguide-drawer-overlay');
    const panel = document.getElementById('pyguide-drawer-panel');
    const closeBtn = document.getElementById('btn-pyguide-drawer-close');
    const searchInput = document.getElementById('pyguide-drawer-search-input');
    const btnSecKit = document.getElementById('btn-drawer-sec-kit');
    const btnSecGen = document.getElementById('btn-drawer-sec-general');
    const kitSelect = document.getElementById('pyguide-drawer-kit-select');

    // Đóng khi click ra vùng overlay bên ngoài panel
    if (overlay && panel) {
      overlay.addEventListener('click', (e) => {
        if (!panel.contains(e.target)) {
          this.close();
        }
      });
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    // Phím Esc đóng drawer
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        this.searchKeyword = (e.target.value || '').trim().toLowerCase();
        this.renderBody();
      });
    }

    if (btnSecKit && btnSecGen) {
      btnSecKit.addEventListener('click', () => {
        this.activeSection = 'kit';
        btnSecKit.classList.add('active');
        btnSecGen.classList.remove('active');
        if (kitSelect) kitSelect.style.display = 'block';
        this.renderBody();
      });

      btnSecGen.addEventListener('click', () => {
        this.activeSection = 'general';
        btnSecGen.classList.add('active');
        btnSecKit.classList.remove('active');
        if (kitSelect) kitSelect.style.display = 'none';
        this.renderBody();
      });
    }

    if (kitSelect) {
      kitSelect.addEventListener('change', (e) => {
        this.selectedKitId = e.target.value;
        this.renderBody();
      });
    }
  }

  renderBody() {
    const body = document.getElementById('pyguide-drawer-body');
    if (!body) return;

    let categories = [];
    if (this.activeSection === 'general') {
      categories = this.data.generalSection?.categories || [];
    } else {
      const curKit = (this.data.kits || []).find(k => k.id === this.selectedKitId) || this.data.kits?.[0];
      categories = curKit?.categories || [];
    }

    let html = '';
    let totalCommandsRendered = 0;

    categories.forEach(cat => {
      const filteredCmds = (cat.commands || []).filter(cmd => this.matchesSearch(cmd));
      if (filteredCmds.length === 0) return;

      totalCommandsRendered += filteredCmds.length;

      html += `
        <div style="display: flex; flex-direction: column; gap: 8px;">
          <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid rgba(255,255,255,0.08); padding-bottom: 4px;">
            <div style="font-size: 12px; font-weight: 700; color: ${cat.badgeColor || '#38BDF8'}; text-transform: uppercase; letter-spacing: 0.5px; display: flex; align-items: center; gap: 6px;">
              <span style="display: inline-block; width: 6px; height: 6px; border-radius: 50%; background: ${cat.badgeColor || '#38BDF8'};"></span>
              <span>${this.escapeHtml(cat.name)}</span>
            </div>
            <span style="font-size: 11px; color: #64748B;">${filteredCmds.length} lệnh</span>
          </div>

          <div style="display: flex; flex-direction: column; gap: 10px;">
            ${filteredCmds.map(cmd => this.renderCommandCard(cmd)).join('')}
          </div>
        </div>
      `;
    });

    if (totalCommandsRendered === 0) {
      html = `
        <div style="padding: 40px 16px; text-align: center; color: #94A3B8;">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="margin-bottom: 8px; opacity: 0.5;"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
          <div style="font-size: 14px; font-weight: 600; color: #fff; margin-bottom: 4px;">Không tìm thấy lệnh nào</div>
          <div style="font-size: 12px;">Thử tìm với từ khóa khác như "move", "speed", "while", "led"...</div>
        </div>
      `;
    }

    body.innerHTML = html;

    // Gắn sự kiện copy mã mẫu
    body.querySelectorAll('.drawer-btn-copy').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const code = btn.getAttribute('data-code');
        if (code) {
          navigator.clipboard.writeText(code).then(() => {
            const originalText = btn.innerHTML;
            btn.classList.add('copied');
            btn.innerHTML = `
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              <span>Đã copy!</span>
            `;
            setTimeout(() => {
              btn.classList.remove('copied');
              btn.innerHTML = originalText;
            }, 1600);
          }).catch(err => {
            console.error('Không thể copy code:', err);
          });
        }
      });
    });
  }

  renderCommandCard(cmd) {
    return `
      <div class="drawer-command-card" id="drawer-cmd-${this.escapeHtml(cmd.id)}">
        <div style="display: flex; align-items: baseline; justify-content: space-between; gap: 8px;">
          <div class="drawer-cmd-name">${this.escapeHtml(cmd.name)}</div>
          ${cmd.returns && cmd.returns !== 'Không' && cmd.returns !== 'None' ? `
            <span style="font-size: 10px; color: #34D399; background: rgba(16, 185, 129, 0.12); padding: 1px 5px; border-radius: 4px; font-family: 'JetBrains Mono', monospace;">
              &rarr; ${this.escapeHtml(cmd.returns)}
            </span>
          ` : ''}
        </div>

        <div class="drawer-cmd-desc">${this.escapeHtml(cmd.description)}</div>

        <!-- Cú pháp gọi -->
        <div class="drawer-syntax-box">${this.escapeHtml(cmd.syntax)}</div>

        <!-- Bảng tham số rút gọn (nếu có) -->
        ${cmd.params && cmd.params.length > 0 ? `
          <div style="background: rgba(0,0,0,0.3); border-radius: 6px; padding: 6px 8px; font-size: 11px;">
            <div style="font-weight: 700; color: #94A3B8; margin-bottom: 3px;">Tham số:</div>
            ${cmd.params.map(p => `
              <div style="display: flex; gap: 6px; margin-bottom: 2px;">
                <span style="color: #38BDF8; font-family: 'JetBrains Mono', monospace; font-weight: 600;">${this.escapeHtml(p.name)}</span>
                <span style="color: #64748B;">(${this.escapeHtml(p.type)})</span>
                <span style="color: #CBD5E1;">${this.escapeHtml(p.desc)}</span>
              </div>
            `).join('')}
          </div>
        ` : ''}

        <!-- Code mẫu đi kèm -->
        ${cmd.example ? `
          <div class="drawer-example-box">
            <div class="drawer-example-header">
              <span>Code mẫu</span>
              <button class="drawer-btn-copy" type="button" data-code="${this.escapeHtml(cmd.example)}">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                <span>Chép code</span>
              </button>
            </div>
            <pre class="drawer-example-pre"><code>${this.escapeHtml(cmd.example)}</code></pre>
          </div>
        ` : ''}

        <!-- Ghi chú nếu có -->
        ${cmd.note ? `
          <div style="font-size: 11px; color: #F59E0B; background: rgba(245, 158, 11, 0.08); border-left: 2px solid #F59E0B; padding: 4px 8px; border-radius: 0 4px 4px 0;">
            <strong style="font-weight: 700;">Lưu ý:</strong> ${this.escapeHtml(cmd.note)}
          </div>
        ` : ''}
      </div>
    `;
  }

  matchesSearch(cmd) {
    if (!this.searchKeyword) return true;
    const kw = this.searchKeyword;
    return (
      (cmd.name || '').toLowerCase().includes(kw) ||
      (cmd.syntax || '').toLowerCase().includes(kw) ||
      (cmd.description || '').toLowerCase().includes(kw) ||
      (cmd.example || '').toLowerCase().includes(kw)
    );
  }

  open() {
    this.isOpen = true;
    const overlay = document.getElementById('pyguide-drawer-overlay');
    if (overlay) {
      overlay.classList.add('open');
    }
    // Nạp lại dữ liệu mới nhất khi mở
    this.loadData();

    // Tự động focus vào thanh tìm kiếm trên màn hình lớn
    if (window.innerWidth > 768) {
      setTimeout(() => {
        const input = document.getElementById('pyguide-drawer-search-input');
        if (input) input.focus();
      }, 150);
    }
  }

  close() {
    this.isOpen = false;
    const overlay = document.getElementById('pyguide-drawer-overlay');
    if (overlay) {
      overlay.classList.remove('open');
    }
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  escapeHtml(str) {
    if (!str || typeof str !== 'string') return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
